<template>
  <header class="fixed top-0 left-0 right-0 z-[300] bg-[#0b1220]/90 backdrop-blur-md border-b border-white/10">
    <div class="container mx-auto px-4 lg:px-8">
      <nav class="flex items-center justify-between h-20">
        <!-- Logo -->
        <router-link to="/" class="flex items-center gap-3 group">
          <div class="w-10 h-10 bg-[var(--color-primary)] rounded-xl flex items-center justify-center transition-transform group-hover:scale-105">
            <span class="text-white font-bold text-xl">X</span>
          </div>
          <div class="flex flex-col">
            <span class="text-lg xl:text-xl font-bold text-white leading-tight whitespace-nowrap">{{ $t('logoText') }}</span>
            <!-- Latin locales already say XIONGSU on the main line — skip the duplicate tagline -->
            <span v-if="$t('logoText') !== 'XIONGSU'" class="text-xs font-semibold text-[var(--color-accent-bright)] tracking-wider">XIONGSU</span>
          </div>
        </router-link>

        <!-- Desktop Nav — 8 items × 8 locales: spacing lives in the link padding
             and the font scales fluidly (13px @1024 → 15px @≥1440) so long-word
             languages (de/es/tr…) fit without wrapping or crowding. -->
        <div class="hidden lg:flex items-center min-w-0 mx-2">
          <ul class="flex items-center gap-0 xl:gap-1">
            <li v-for="link in navLinks" :key="link.to">
              <router-link :to="link.to" class="nav-link nav-fluid text-steel-300 font-medium hover:text-white transition-colors relative py-2 px-2 xl:px-3 whitespace-nowrap">
                {{ $t(link.label) }}
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Right: Cart + Lang + Theme + CTA -->
        <div class="hidden lg:flex items-center gap-1.5 xl:gap-2 flex-shrink-0">
          <button @click="openCart" :aria-label="$t('cart_open')"
            class="relative p-2.5 rounded-lg text-steel-300 hover:bg-white/10 hover:text-white transition-colors">
            <ShoppingCart class="w-5 h-5" />
            <span v-if="cartCount"
              class="absolute -top-0.5 -end-0.5 min-w-[18px] h-[18px] px-1 bg-[var(--color-primary)] text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
              {{ cartCount }}
            </span>
          </button>
          <ThemeSwitcher />
          <LanguageSwitcher />
          <router-link to="/quote" class="inline-flex items-center gap-2 px-3.5 xl:px-5 py-2.5 bg-[var(--color-primary)] text-white rounded-lg font-medium hover:bg-[var(--color-primary-dark)] transition-colors text-sm whitespace-nowrap">
            {{ $t('navCTA') }}
          </router-link>
        </div>

        <!-- Mobile: cart + menu button -->
        <div class="lg:hidden flex items-center gap-1">
          <button @click="openCart" :aria-label="$t('cart_open')"
            class="relative p-2 rounded-lg text-steel-300 hover:bg-white/10">
            <ShoppingCart class="w-6 h-6" />
            <span v-if="cartCount"
              class="absolute top-0 end-0 min-w-[18px] h-[18px] px-1 bg-[var(--color-primary)] text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
              {{ cartCount }}
            </span>
          </button>
          <button @click="mobileOpen = !mobileOpen" class="p-2 rounded-lg text-steel-300 hover:bg-white/10">
            <X v-if="mobileOpen" class="w-6 h-6" />
            <Menu v-else class="w-6 h-6" />
          </button>
        </div>
      </nav>
    </div>

    <!-- Mobile Menu — Teleported to <body>. The <header> has backdrop-blur
         (backdrop-filter), which makes it the containing block for any fixed
         descendant; inside it this panel's `top-[80px] bottom-0` resolves against
         the 80px-tall header and collapses to 0 height (invisible on mobile).
         Teleporting to <body> sizes the fixed panel against the viewport. -->
    <Teleport to="body">
      <transition name="slide">
        <div v-if="mobileOpen" class="lg:hidden fixed inset-x-0 top-[80px] bottom-0 bg-white z-[402] overflow-y-auto">
        <div class="container mx-auto px-4 py-6">
          <!-- Mobile Language (8 buttons — must wrap on narrow screens) -->
          <div class="flex flex-wrap items-center justify-center gap-2 mb-6 pb-6 border-b border-[#e2e8f0]">
            <button v-for="lang in mobileLangs" :key="lang.code" @click="switchLang(lang.code)"
              :class="['px-4 py-2 rounded-lg text-sm font-medium transition-all', locale === lang.code ? 'bg-[var(--color-primary)] text-white' : 'bg-[#f7fafc] text-[#4a5568]']">
              {{ $t(lang.labelKey) }}
            </button>
          </div>
          <!-- Mobile Theme + Cart -->
          <div class="flex items-center justify-center gap-3 mb-6 pb-6 border-b border-[#e2e8f0]">
            <ThemeSwitcher />
            <button @click="openCart"
              class="relative inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#f7fafc] text-[#4a5568] text-sm font-medium hover:text-[var(--color-primary)] transition-colors">
              <ShoppingCart class="w-5 h-5" />
              {{ $t('cart_open') }}
              <span v-if="cartCount"
                class="min-w-[18px] h-[18px] px-1 bg-[var(--color-primary)] text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                {{ cartCount }}
              </span>
            </button>
          </div>
          <ul class="space-y-1">
            <li v-for="link in navLinks" :key="link.to">
              <router-link :to="link.to" @click="mobileOpen = false"
                class="flex items-center gap-3 px-4 py-3 rounded-xl text-[#4a5568] hover:bg-[var(--color-primary-light)] hover:text-[var(--color-primary)] transition-all font-medium">
                {{ $t(link.label) }}
              </router-link>
            </li>
          </ul>
          <div class="mt-6 pt-6 border-t border-[#e2e8f0]">
            <router-link to="/quote" @click="mobileOpen = false"
              class="block w-full text-center px-5 py-3 bg-[var(--color-primary)] text-white rounded-lg font-medium">
              {{ $t('navCTA') }}
            </router-link>
          </div>
        </div>
      </div>
      </transition>
    </Teleport>
  </header>
  <div class="h-20"></div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { Menu, X, ShoppingCart } from 'lucide-vue-next'
import LanguageSwitcher from './LanguageSwitcher.vue'
import ThemeSwitcher from './ThemeSwitcher.vue'
import { cartCount } from '../services/cart'

const { locale } = useI18n()
const route = useRoute()
const mobileOpen = ref(false)

// Single CartDrawer instance is mounted in App.vue and opens on this window event.
function openCart() {
  mobileOpen.value = false
  window.dispatchEvent(new CustomEvent('xs-open-cart'))
}

// Close the mobile menu on ANY navigation (logo click, back/forward, programmatic
// nav), not just the explicit per-link handlers below (kept for instant feedback).
watch(() => route.fullPath, () => { mobileOpen.value = false })

const navLinks = [
  { to: '/', label: 'navHome' },
  { to: '/products', label: 'navProducts' },
  { to: '/about', label: 'navAbout' },
  { to: '/bases', label: 'navBases' },
  { to: '/quality', label: 'navQuality' },
  { to: '/global', label: 'navGlobal' },
  { to: '/news', label: 'navNews' },
  { to: '/contact', label: 'navContact' }
]

const mobileLangs = [
  { code: 'zh-CN', labelKey: 'langZh' },
  { code: 'en', labelKey: 'langEn' },
  { code: 'fr', labelKey: 'langFr' },
  { code: 'de', labelKey: 'langDe' },
  { code: 'it', labelKey: 'langIt' },
  { code: 'es', labelKey: 'langEs' },
  { code: 'tr', labelKey: 'langTr' },
  { code: 'ar', labelKey: 'langAr' }
]

function switchLang(code) { locale.value = code }
</script>

<style scoped>
/* Fluid nav type: 13px at 1024px viewport, easing up to 15px by ~1570px.
   Keeps all 8 items on one uncrowded line in every locale. */
.nav-fluid {
  font-size: clamp(13px, 9.5px + 0.35vw, 15px);
}
.nav-link::after {
  content: ''; position: absolute; bottom: 0; left: 0; width: 100%; height: 2px;
  background-color: var(--color-accent-bright); transform: scaleX(0); transition: transform 0.3s ease;
}
.nav-link:hover::after, .router-link-exact-active::after { transform: scaleX(1); }
.router-link-exact-active { color: var(--color-accent-bright); }
.slide-enter-active, .slide-leave-active { transition: transform 0.3s ease; }
.slide-enter-from, .slide-leave-to { transform: translateX(100%); }
</style>
